import { create } from 'zustand'
import type { CrmebUser, CartItem, UserAddress, PaymentQrcodeStatus, SelectedPromotionBundle } from '@/api/admin-order'

export interface ManualAddress {
  real_name: string
  phone: string
  province: string
  city: string
  district: string
  city_id: number
  detail: string
  save_as_common: boolean
}

interface OrderCreateState {
  currentStep: number
  setStep: (step: number) => void
  nextStep: () => void
  prevStep: () => void

  selectedUser: CrmebUser | null
  setSelectedUser: (user: CrmebUser) => void

  cartItems: CartItem[]
  addCartItem: (item: CartItem) => void
  removeCartItem: (index: number) => void
  updateCartQuantity: (index: number, qty: number) => void
  clearCart: () => void

  bundleItems: SelectedPromotionBundle[]
  addBundleItem: (item: SelectedPromotionBundle) => void
  removeBundleItem: (index: number) => void
  updateBundleQuantity: (index: number, qty: number) => void
  clearBundles: () => void

  selectedAddress: UserAddress | null
  setSelectedAddress: (addr: UserAddress | null) => void
  manualAddress: ManualAddress | null
  setManualAddress: (addr: ManualAddress | null) => void
  useManualAddress: boolean
  setUseManualAddress: (use: boolean) => void

  shippingType: number
  setShippingType: (type: number) => void
  mark: string
  setMark: (mark: string) => void
  isStore: boolean
  setStore: (value: boolean) => void
  isCash: boolean
  setCash: (value: boolean) => void
  shippingPartial: boolean
  setShippingPartial: (value: boolean) => void
  shippingRemark: string
  setShippingRemark: (value: string) => void

  createdOrderId: string
  setCreatedOrderId: (id: string) => void
  createdPayPrice: number
  setCreatedPayPrice: (price: number) => void
  paymentQrcodeStatus: PaymentQrcodeStatus | ''
  paymentQrcode: string
  paymentDepartmentId: number
  paymentDepartmentName: string
  setPaymentQrcodeInfo: (info: {
    status: PaymentQrcodeStatus
    qrcode: string
    departmentId: number
    departmentName: string
  }) => void

  reset: () => void
}

const initialState = {
  currentStep: 0,
  selectedUser: null as CrmebUser | null,
  cartItems: [] as CartItem[],
  bundleItems: [] as SelectedPromotionBundle[],
  selectedAddress: null as UserAddress | null,
  manualAddress: null as ManualAddress | null,
  useManualAddress: false,
  shippingType: 1,
  mark: '',
  isStore: false,
  isCash: false,
  shippingPartial: false,
  shippingRemark: '',
  createdOrderId: '',
  createdPayPrice: 0,
  paymentQrcodeStatus: '' as PaymentQrcodeStatus | '',
  paymentQrcode: '',
  paymentDepartmentId: 0,
  paymentDepartmentName: '',
}

export const useOrderCreateStore = create<OrderCreateState>()((set) => ({
  ...initialState,

  setStep: (step) => set({ currentStep: step }),
  nextStep: () => set((state) => ({ currentStep: Math.min(state.currentStep + 1, 4) })),
  prevStep: () => set((state) => ({ currentStep: Math.max(state.currentStep - 1, 0) })),

  setSelectedUser: (user) => set({ selectedUser: user }),

  addCartItem: (item) =>
    set((state) => {
      const existing = state.cartItems.findIndex((i) => i.unique === item.unique)
      if (existing >= 0) {
        const updated = [...state.cartItems]
        updated[existing] = { ...updated[existing], cartNum: updated[existing].cartNum + item.cartNum }
        return { cartItems: updated }
      }
      return { cartItems: [...state.cartItems, item] }
    }),
  removeCartItem: (index) =>
    set((state) => ({ cartItems: state.cartItems.filter((_, i) => i !== index) })),
  updateCartQuantity: (index, qty) =>
    set((state) => {
      const updated = [...state.cartItems]
      updated[index] = { ...updated[index], cartNum: qty }
      return { cartItems: updated }
    }),
  clearCart: () => set({ cartItems: [] }),

  addBundleItem: (item) =>
    set((state) => {
      const existing = state.bundleItems.findIndex((i) => i.bundleId === item.bundleId)
      if (existing >= 0) {
        const updated = [...state.bundleItems]
        updated[existing] = { ...updated[existing], quantity: updated[existing].quantity + item.quantity }
        return { bundleItems: updated }
      }
      return { bundleItems: [...state.bundleItems, item] }
    }),
  removeBundleItem: (index) =>
    set((state) => ({ bundleItems: state.bundleItems.filter((_, i) => i !== index) })),
  updateBundleQuantity: (index, qty) =>
    set((state) => {
      const updated = [...state.bundleItems]
      updated[index] = { ...updated[index], quantity: qty }
      return { bundleItems: updated }
    }),
  clearBundles: () => set({ bundleItems: [] }),

  setSelectedAddress: (addr) => set({ selectedAddress: addr }),
  setManualAddress: (addr) => set({ manualAddress: addr }),
  setUseManualAddress: (use) => set({ useManualAddress: use }),

  setShippingType: (type) => set({ shippingType: type }),
  setMark: (mark) => set({ mark }),
  setStore: (value) => set({ isStore: value }),
  setCash: (value) => set({ isCash: value }),
  setShippingPartial: (value) => set({ shippingPartial: value }),
  setShippingRemark: (value) => set({ shippingRemark: value }),

  setCreatedOrderId: (id) => set({ createdOrderId: id }),
  setCreatedPayPrice: (price) => set({ createdPayPrice: price }),
  setPaymentQrcodeInfo: (info) => set({
    paymentQrcodeStatus: info.status,
    paymentQrcode: info.qrcode,
    paymentDepartmentId: info.departmentId,
    paymentDepartmentName: info.departmentName,
  }),

  reset: () => set(initialState),
}))
